import { configureStore } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { combineReducers } from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
  createTransform,
} from 'redux-persist';
import { encryptTransform } from 'redux-persist-transform-encrypt';

import { authSlice } from './slice/authSlice';
import { userSlice } from './slice/userSlice';
import { menusSlice } from './slice/menusSlice';
import { lookupSlice } from './slice/lookupSlice';
import { cardOrderSlice } from './slice/cardOrderSlice';
import { ticketCarCardOrderSlice } from './slice/ticketCarCardOrderSlice';
import { addressSlice } from './slice/addressSlice';
import { cardOrderDetailsSlice } from './slice/cardOrderDetailtsSlice';
import { mobilExpressSlice } from './slice/mobilExpressSlice';
import { balanceUploadSlice } from './slice/balanceUploadSlice';
import { customerSlice } from './slice/customerSlice';
import { draftOrderSlice } from './slice/draftOrderSlice';
import { balanceUploadDetailSlice } from './slice/balanceUploadDetailSlice';
import { orderSlice } from './slice/orderSlice';
import { manageMyCardsSlice } from './slice/manageMyCardsSlice';
import { balanceTransferSlice } from './slice/balanceTransferSlice';
import { autoUploadInstructionsSlice } from './slice/autoUploadInstructionsSlice';
import { appSettingsSlice } from './slice/appSettingsSlice';
import { bankSlice } from './slice/bankSlice';
import { framesSlice } from './slice/framesSlice';
import { invoiceSlice } from './slice/invoiceSlice';
import { companyInfosSlice } from './slice/companyInfosSlice';
import { quickAccessLinkSlice } from './slice/quickAccessLinkSlice';
import { cardBalancesSlice } from './slice/cardBalancesSlice';
import { cardImagesSlice } from './slice/cardImagesSlice';
import { orderLimitsSlice } from './slice/orderLimitsSlice';
import { packagesSlice } from './slice/packagesSlice';
import { custUsersSlice } from './slice/custUsersSlice';
import { contactsSlice } from './slice/contactsSlice';
import { customerVouGroupsSlice } from './slice/customerVouGroupsSlice';
import { groupsSlice } from './slice/groupsSlice';
import { userTypesSlice } from './slice/userTypesSlice';
import { notificationsSlice } from './slice/notificationSlice';
import { notificationsDashboardSlice } from './slice/notificationDashboardSlice';
import { managementLanguagesSlice } from './slice/managementLanguageSlice';
import { managementAppSettingsSlice } from './slice/managementAppSettingsSlice';
import { managementBankSlice } from './slice/managementBankSlice';
import { operationClaimsSlice } from './slice/operationClaimsSlice';
import { vousSlice } from './slice/vousSlice';
import { paymentMethodsSlice } from './slice/paymentMethodsSlice';
import { userTypeGroupsSlice } from './slice/userTypeGroupsSlice';
import { specialAuthorizationsSlice } from './slice/specialAuthorizationsSlice';
import { searchBarsSlice } from './slice/searchBarsSlice';
import { translateMapSlice } from './slice/translateMapsSlice';
import { faceValuePopupSlice } from './slice/faceValuePopupSlice';
import { addCarTypeSlice } from './slice/addNewCarStore/addCarTypeSlice';
import { addCarPlateInformationSlice } from './slice/addNewCarStore/addCarPlateInformationSlice';
import { addCarPlateDetailSlice } from './slice/addNewCarStore/addCarPlateDetailSlice';
import { vehicleManagementSlice } from './slice/vehicleManagementStore/vehicleManagementSlice';
import { vehicleManagementDetailSlice } from './slice/vehicleManagementStore/vehicleManagementDetailSlice';
import { demandsSlice } from './slice/demandsStore/demandsSlice';
import { newCarDemandsSlice } from './slice/newCarDemandsStore/newCarDemandsSlice';
import { fuelConsumptionSlice } from './slice/fuelConsumptionStore/fuelConsumptionSlice';
import { errorListSlice } from './slice/errorListStore/errorListSlice';
import { fleetListSlice } from './slice/fleetListStore/fleetListSlice';
import { costCenterSlice } from './slice/costCenterStore/costCenterSlice';
import { addFleetLimitSlice } from './slice/addFleetLimitStore/addFleetLimitSlice';

const reducers = combineReducers({
  auth: authSlice?.reducer,
  addCarType: addCarTypeSlice?.reducer,
  addCarPlateInformation: addCarPlateInformationSlice?.reducer,
  addCarPlateDetail: addCarPlateDetailSlice?.reducer,
  vehicleManagementDetail: vehicleManagementDetailSlice?.reducer,
  vehicleManagement: vehicleManagementSlice?.reducer,
  user: userSlice?.reducer,
  customer: customerSlice?.reducer,
  menus: menusSlice?.reducer,
  lookup: lookupSlice?.reducer,
  address: addressSlice?.reducer,
  bank: bankSlice?.reducer,
  cardOrder: cardOrderSlice?.reducer,
  ticketCarCardOrder: ticketCarCardOrderSlice?.reducer,
  cardOrderDetails: cardOrderDetailsSlice?.reducer,
  mobilExpress: mobilExpressSlice?.reducer,
  balanceUpload: balanceUploadSlice?.reducer,
  balanceUploadDetail: balanceUploadDetailSlice?.reducer,
  draftOrder: draftOrderSlice?.reducer,
  order: orderSlice?.reducer,
  manageMyCards: manageMyCardsSlice?.reducer,
  balanceTransfer: balanceTransferSlice?.reducer,
  autoUploadInstructions: autoUploadInstructionsSlice?.reducer,
  appSettings: appSettingsSlice?.reducer,
  frames: framesSlice?.reducer,
  invoice: invoiceSlice?.reducer,
  companyInfos: companyInfosSlice?.reducer,
  quickAccessLink: quickAccessLinkSlice?.reducer,
  cardBalances: cardBalancesSlice?.reducer,
  cardImages: cardImagesSlice?.reducer,
  orderLimits: orderLimitsSlice?.reducer,
  packages: packagesSlice?.reducer,
  custUsers: custUsersSlice?.reducer,
  contacts: contactsSlice?.reducer,
  customerVouGroups: customerVouGroupsSlice?.reducer,
  groups: groupsSlice?.reducer,
  userTypes: userTypesSlice?.reducer,
  notifications: notificationsSlice?.reducer,
  notificationsDashboard: notificationsDashboardSlice?.reducer,
  managementLanguages: managementLanguagesSlice?.reducer,
  managementAppSettings: managementAppSettingsSlice?.reducer,
  managementBank: managementBankSlice?.reducer,
  operationClaims: operationClaimsSlice?.reducer,
  vous: vousSlice?.reducer,
  paymentMethods: paymentMethodsSlice?.reducer,
  userTypeGroups: userTypeGroupsSlice?.reducer,
  specialAuthorizations: specialAuthorizationsSlice?.reducer,
  searchBars: searchBarsSlice?.reducer,
  translateMap: translateMapSlice?.reducer,
  faceValuePopup: faceValuePopupSlice?.reducer,
  demands: demandsSlice?.reducer,
  errorList: errorListSlice?.reducer,
  fleetList: fleetListSlice?.reducer,
  newCarDemands: newCarDemandsSlice?.reducer,
  fuelConsumption: fuelConsumptionSlice?.reducer,
  costCenter: costCenterSlice?.reducer,
  addFleetLimit: addFleetLimitSlice?.reducer,
});

const authTransform = createTransform(
  (inboundState) => {
    if (inboundState?.username) {
      const { username, ...rest } = inboundState;
      return rest;
    }
    return inboundState;
  },

  (outboundState) => { return outboundState; },

  { whitelist: ['auth'] }
);

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['auth', 'user', 'customer'], // only persist these keys,
  transforms: [
    encryptTransform({
      secretKey: process.env.VITE_REDUX_SECRET_KEY || 'xRjxUq7gu6',
      onError: function (error) {
        throw new Error(error?.message);
      },
    }),
    authTransform,
  ],
};

const rootReducer = (state, action) => {
  if (action.type === FLUSH) {
    storage.removeItem('persist:root');
    return reducers(undefined, action);
  }

  return reducers(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: true, //process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });
  },
});

const persist = persistStore(store);

export { store, persist };
